import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { animated, useSpring } from '@react-spring/web';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import pluralize from 'pluralize';
import { nanoid } from 'nanoid';

import { hideSideMenu } from 'src/redux/actions/hub';
import { createAssessmentConfiguration, fetchAssessmentConfiguration, fetchAssessmentConfigurations, invalidateAssessmentConfigurationCreation, invalidateAssessmentConfigurationCreationState, invalidateAssessmentConfigurationDeletionState, invalidateAssessmentConfigurations, invalidateAssessmentConfigurationUpdateState, updateAssessmentConfiguration } from 'src/redux/actions/coaching/settings';

import ScoreCardPreview, { CallTypeDropdown } from './preview';

import Slider from 'src/components/slider';
import Button from 'src/components/button';
import { Input } from 'src/components/input';
import SwitchButton from 'src/components/switchButton';
import Text from 'src/components/text';
import Reveal from 'src/components/animation';

import { EditChange, ScoreCardsContext, ScoreCardsContextProps } from './context';
import { CloneHelpToolbar, CloneMenubar, DeleteMenubar, DeleteSelectionToolbar, RenameMenubar, ScoreCardsMenuBar } from './edit';
import { AssessmentConfiguration, AssessmentParameter } from 'src/types/coaching/assessmentConfiguration';

import _ from "lodash";
import cx from "classnames";
import styles from "./scoreCards.module.scss"


const ScoreCardsPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { loading, scorecards, creating, created } = useSelector((state: any) => {
    const scoreCardsState = _.get(state, 'coaching.settings.scorecards');

    const scorecards: AssessmentConfiguration[] = _.get(scoreCardsState, 'items') || [];
    const loading = _.get(scoreCardsState, 'loading', false);   
    
    const creating = _.get(scoreCardsState, 'creation.creating', false);
    const created: AssessmentConfiguration | null = _.get(scoreCardsState, 'creation.item', null);

    return {
      scorecards,
      loading,
      creating,
      created,
    };
  }, _.isEqual);

  const handlePreview = (item: any) => {
    setScoreCardsContext((prev) => ({
      ...prev,
      scorecard: item,
      showPreview: true,
    }));
    dispatch(hideSideMenu(true));
    dispatch(fetchAssessmentConfiguration(item.id));

    navigate(`/settings/call-assessments/${item.id}`)
  };

  const handleRename = () => {
    const editedScorecards = scoreCardsContext.edits.filter((edit) => {
      if (edit.value != null && _.size((edit.value as AssessmentConfiguration)?.name!.trim()) > 0) {
        return true;
      }

      return false;
    });

    editedScorecards.forEach((edit) => {
      dispatch(
        updateAssessmentConfiguration({
          id: edit.id,
          request: { name: (edit.value as AssessmentConfiguration)?.name!.trim() },
        }),
      );
    });

    if (_.size(editedScorecards) == 0) {
      handleEditModeChange(null);
    }
  };

  const closePreview = () => {
    dispatch(hideSideMenu(false));
    setScoreCardsContext((prev) => ({
      ...prev,
      showPreview: false,
      scorecard: null,
    }));
  }

  const [scoreCardsContext, setScoreCardsContext] = useState<ScoreCardsContextProps>({
    scorecard: null,
    editMode: null,
    edits: [],
    showPreview: false,
    onEditChanges: () => { },
    onEditModeChange: () => { },
  });  

  const handleCreateScorecard = (name, description) => {
    const newScorecard = {
      name,
      description,
      callType: ['all'],
      parameters: [
        {
          uid: nanoid(6),
          name: 'Untitled',
          weightage: 0,
          parameters: [
            {
              uid: nanoid(6),
              name: 'Untitled',
              criteria: '',
            },
          ]
        }
      ],
      defaultConfiguration: false,
      systemDefined: false,
    };

    dispatch(createAssessmentConfiguration(newScorecard));
  };  

  const handleEditChanges = (change: EditChange) => {
    setScoreCardsContext((prev) => {
      const edits = prev.edits.filter(
        (e) => !(e.id === change.id)
      );

      return {
        ...prev,
        edits: [...edits, change],
      };
    });
  };

  const handleEditModeChange = (value: any) => {
    setScoreCardsContext((state) => {
      const edits = value != null ? state.edits : [];  
      if (!value) {
        dispatch(invalidateAssessmentConfigurationUpdateState());
        dispatch(invalidateAssessmentConfigurationDeletionState());
        dispatch(invalidateAssessmentConfigurationCreationState());
      }
  
      return {
        ...state,
        editMode: value,
        edits: edits
      };
    });
  };

  useEffect(() => {
    if(_.size(scorecards) === 0) {
      dispatch(fetchAssessmentConfigurations({callType: 'all'}));
    }
  }, []);

  useEffect(() => {
    setScoreCardsContext((prev) => ({
      ...prev,
      onEditChanges: handleEditChanges,
      onEditModeChange: handleEditModeChange,
      onClosePreview: closePreview,
    }));

    return () => {
      dispatch(invalidateAssessmentConfigurations());
    };
  }, []);

  useEffect(() => {
    if (created) {
      setScoreCardsContext((prev) => ({
        ...prev,
        scorecard: created,
        showPreview: true,
      }));
      
      dispatch(invalidateAssessmentConfigurationCreation());
      dispatch(hideSideMenu(true));
      handleEditModeChange(null);

      navigate(`/settings/call-assessments/${created.id}`);
    }
  }, [created]);

  return (
    <ScoreCardsContext.Provider value={scoreCardsContext}>
      {!scoreCardsContext.showPreview && (
        <div className={styles.container}>
          <div className={styles.top}>
            <div className={styles.header}>
              <h1 className={styles.title}>call assessment frameworks</h1>
              <p>
                Define the evaluation criteria used to assess learner
                performance across call simulations and real calls.
              </p>
            </div>
            <div className={styles.actionMenu}>
              <ScoreCardsMenuBar
                show={
                  !["rename", "delete", "clone"].includes(
                    scoreCardsContext.editMode || "",
                  )
                }
                handleEditModeChange={handleEditModeChange}
              />
              <RenameMenubar
                show={scoreCardsContext.editMode === "rename"}
                onCancel={() => handleEditModeChange(null)}
                onConfirm={handleRename}
              />
              <DeleteMenubar
                show={scoreCardsContext.editMode === "delete"}
                onCancel={() => {}}
              />
              <CloneMenubar
                show={scoreCardsContext.editMode === "clone"}
                onCancel={() => handleEditModeChange(null)}
              />
            </div>
          </div>
          <div className={styles.body}>
            <div className={styles.list}>
            {loading && <PlaceholderScoreCard />}
            {!loading &&
              scorecards.map((scorecard: AssessmentConfiguration) => (
                <ScoreCard
                  key={scorecard.id}
                  scorecard={scorecard}
                  onClick={() => handlePreview(scorecard)}
                />
              ))}
              </div>
          </div>
        </div>
      )}
      {scoreCardsContext.showPreview && <ScoreCardPreview />}

      <DeleteSelectionToolbar
        show={scoreCardsContext.editMode === "delete"}
        scorecards={scoreCardsContext.edits
          .filter((edit) => edit.value)
          .map((edit) => edit.value as AssessmentConfiguration)}
        onFinish={() => {
          handleEditModeChange(null);
        }}
      />
      <CloneHelpToolbar show={scoreCardsContext.editMode === "clone"} />
      <CreateScoreCardDialog
        show={scoreCardsContext.editMode === "create"}
        onClose={() => handleEditModeChange(null)}
        creating={creating}
        onCreate={handleCreateScorecard}
      />
    </ScoreCardsContext.Provider>
  );
};

export default ScoreCardsPage;

interface ScoreCardProps {
  scorecard: AssessmentConfiguration;
  onClick?: () => void;
}

export const ScoreCard: React.FC<ScoreCardProps> = ({ scorecard, onClick}) => {

  const { editMode, onEditChanges, edits } = useContext(ScoreCardsContext);

  const parameters = _.get(scorecard, 'parameters', []) as AssessmentParameter[];
  const parameterCount = _.size(parameters);

  let subParameterCount = 0;
  parameters.forEach(p => {
    subParameterCount += _.size(p.parameters);
  });

  const [draftScorecard, setDraftScorecard] = useState<AssessmentConfiguration>(scorecard);

  const CALL_TYPE_OPTIONS = [
    { label: 'all type', value: 'all' },
    { label: 'cold call', value: 'cold_call' },
    { label: 'discovery', value: 'discovery' },
    { label: 'follow-up', value: 'follow_up' },
  ];

  const getCallTypeLabel = (callType: string) => {
    // default to 0th index for. Data supports multuple call types for later use cases
    const option = CALL_TYPE_OPTIONS.find(item => item.value === callType);
    return option ? option.label : 'all type';
  }

  const isDefault = scorecard.defaultConfiguration;
  const isReadOnly = scorecard.systemDefined;
  const isRename = editMode === 'rename';
  const isCardClickable = editMode === null;
  const selectionMode = _.includes(["delete", "clone"], editMode);

  const selected = edits.some(
    (e) =>
      e.id === scorecard.id &&
      e.mode === editMode &&
      e.value !== null
  );

  const handleSelectionToggle = (e?: React.MouseEvent) => {
    e?.stopPropagation();
  
    if (!selectionMode) {
      return;
    }
  
    if (editMode !== "delete" && editMode !== "clone") {
      return;
    }
  
    onEditChanges({
      id: scorecard.id!,
      mode: editMode,
      value: selected ? null : scorecard,
    });
  };

  const handleCardClick = () => {
    if (selectionMode && !scorecard.systemDefined) {
      handleSelectionToggle();
      return;
    }
  
    if (isCardClickable) {
      onClick?.();
    }
  };
  
  const selectedCallTypeOption = CALL_TYPE_OPTIONS.find(
    (opt) => opt.value === scorecard.callType?.[0]
  );

  const cardSpring = useSpring({
    height: isRename ? 300 : 176,
    config: { tension: 170, friction: 26 },
  });

  return (
    <animated.div
      style={cardSpring}
      className={cx(styles.card, {
        [styles.renameCard]: isRename,
        [styles.selected]: selected,
        [styles.defaultCard]: scorecard.systemDefined,
        [styles.selectionMode]: selectionMode && !scorecard.systemDefined,
      })}
      onClick={handleCardClick}
    >
      {selectionMode && !scorecard.systemDefined && (
        <div
          className={styles.selection}
          onClick={(e) => {
            e.stopPropagation();
            handleSelectionToggle();
          }}
        >
          {selected && (
            <i className="fa-solid fa-check-circle" />
          )}
          {!selected && (
            <i className="paperflite-radio-button-unselected" />
          )}
        </div>
      )}

      {editMode !== 'rename' && (
        <>
          <div className={styles.header}>
            <div className={styles.status}>
              {scorecard.published && (
                <div className={styles.detail}>
                  <div className={styles.active}></div>
                  <p>Active</p>
                </div>
              )}
              {!scorecard.published && (<div className={styles.detail}>
                  <div className={styles.draft}></div>
                  <p>Draft</p>
                </div>
              )}
            </div>
            <div className={styles.type}>
              <div className={styles.callType}>
                 {getCallTypeLabel(scorecard.callType?.[0] || 'all')}
                 </div>
              {isDefault && <div className={styles.callType}>default</div>}
            </div>
          </div>
          <div className={styles.information}>
            {<div className={styles.name}>
              <Text text={scorecard.name} lines={1} />
              </div>}
            <div className={styles.metadata}>
              <div className={styles.metaItem}>
                <div className={styles.value}>{parameterCount}</div>
                <div className={styles.label}>{pluralize("parameter", parameterCount)}
                </div>
              </div>
              <div className={styles.divider} />
              <div className={styles.metaItem}>
                <div className={styles.value}>{subParameterCount}</div>
                <div className={styles.label}>{pluralize("sub-parameter", subParameterCount)}</div>
              </div>
            </div>
          </div>
          <div className={styles.footer}>
            {scorecard.systemDefined && 'predefined sales methodology framework.'}
            {!scorecard.systemDefined && `Last updated on ${ moment(scorecard.modifiedDate).format("DD MMM YYYY")}`}
          </div>
        </>
      )}
      {editMode === 'rename' && (
        <div className={styles.rename}>
          <div className={styles.header}>
            <div className={styles.status}>
              <div className={styles.isActive}>
              </div>
              <p>Active</p>
            </div>
          </div>
          <Input
            className={styles.renameInput}
            value={draftScorecard.name!}
            disabled={isReadOnly}
            autoFocus
            onChange={(e) => {
              const name = e.target.value;

              const updated = {
                ...draftScorecard,
                name,
              };

              setDraftScorecard(updated);

              onEditChanges({
                id: scorecard.id!,
                mode: 'rename',
                value: updated,
              });
            }}
          />

          <textarea
            className={styles.descriptionInput}
            value={draftScorecard.description || ''}
            placeholder="No description added"
            disabled={isReadOnly}
            onChange={(e) => {
              const description = e.target.value;

              const updated = {
                ...draftScorecard,
                description,
              };

              setDraftScorecard(updated);

              onEditChanges({
                id: scorecard.id!,
                mode: 'rename',
                value: updated,
              });
            }}
          />

          <div className={styles.bottom}>
            <SwitchButton
              id="defaultScoreCard"
              label="set as default"
              className={cx(styles.switch, {
                [styles.active]: isDefault,
                [styles.disabled]: isReadOnly
              })}
              checked={draftScorecard.defaultConfiguration!}
              handleCheck={() => {
                const updated = {
                  ...draftScorecard,
                  defaultConfiguration: !draftScorecard.defaultConfiguration,
                };

                setDraftScorecard(updated);

                onEditChanges({
                  id: scorecard.id!,
                  mode: 'rename',
                  value: updated,
                });
              }}
            />
            <div className={cx(styles.callType, { [styles.disabled]: isReadOnly })}>
                <CallTypeDropdown
                  selected={selectedCallTypeOption}
                  options={CALL_TYPE_OPTIONS}
                  readOnly={isReadOnly}
                  onChange={(newCallType) => {
                    const updated = {
                      ...draftScorecard,
                      callType: newCallType.value,
                    };

                    setDraftScorecard(updated);

                    onEditChanges({
                      id: scorecard.id!,
                      mode: 'rename',
                      value: updated,
                    });
                  }}
                />
            </div>
          </div>
        </div>
      )}
    </animated.div>
  );
}

const PlaceholderScoreCard = ({ count = 3 }) => {
  return (
    <div className={styles.wrapper}>
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className={styles.placeholderCard}>
          <div className={styles.header}>
            <div className={styles.status} />
          </div>
          <div className={styles.sectionTitle} />
          <div className={styles.metricsRow}>
            <div className={styles.metricBlock}>
              <div className={styles.metric} />
              <div className={styles.metricLabel} />
            </div>
            <div className={styles.divider} />

            <div className={styles.metricBlock}>
              <div className={styles.metric} />
              <div className={styles.metricLabel} />
            </div>
          </div>
          <div className={styles.footer} />
        </div>
      ))}
    </div>
  );
};

interface CreateAssessmentConfigurationDialog {
  show: boolean;
  onClose: () => void;
  creating: boolean;
  onCreate: (name: string, description: string) => void;
}

const CreateScoreCardDialog: React.FC<CreateAssessmentConfigurationDialog> = ({ show, onClose, creating, onCreate }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const getNameError = () => {
    if (_.isEmpty(_.trim(name))) return null;
    if (_.size(name) > 100) {
      return 'Name cannot be greater than 100 characters';
    }
    return null;
  };

  const getDescriptionError = () => {
    if (_.isEmpty(_.trim(description))) return null;
    if (_.size(description) > 200) {
      return 'Description cannot be greater than 200 characters';
    }
    return null;
  };

  const nameError = getNameError();
  const descriptionError = getDescriptionError(); 

  const canCreate =
  !creating &&
  !_.isEmpty(_.trim(name)) &&
  !_.isEmpty(_.trim(description)) &&
  !nameError &&
  !descriptionError;

  const handleCreate = () => {
    onCreate(name, description);
  }

  useEffect(() => {
    if (!show) {
      setName('');
      setDescription('');
    }
  }, [show]);

  return (
    <Slider.Dialog
      className={styles.createCollectionDialog}
      width={"380px"}
      show={show}
      onClose={onClose}
    >
      <Slider.Header
        className={styles.header}
        title="create new framework"
        message='Start by naming your framework and briefly describing what it helps evaluate.'
      />
      <Slider.Body className={styles.body}>
        <Input
          autoFocus
          placeholder="enter framework name"
          value={name}
          className={styles.input}
          onChange={(e) => setName(e.target.value)}
        />
        <Reveal show={!!nameError}>
        {nameError && (
          <div className={styles.error}>
              <i className="paperflite-danger" />
              {nameError}
          </div>
        )}
        </Reveal>
        <textarea
          className={styles.inputValue}
          placeholder="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <Reveal show={!!descriptionError}>
        {descriptionError && (
          <div className={styles.error}>
            <i className="paperflite-danger" />
            {descriptionError}
          </div>
        )}
        </Reveal>
      </Slider.Body>
      <Slider.Actions>
        <Button
          icon="paperflite-tick"
          text={creating ? "Creating..." : "Create"}
          variant="primary"
          disabled={!canCreate}
          onClick={handleCreate}
        />
        <Button
          text="Cancel"
          variant="secondary"
          onClick={onClose}
        />
      </Slider.Actions>
    </Slider.Dialog>
  );
}